import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, Save, FileText, Search, Plus, Trash2 } from "lucide-react";

const KIND_LABELS = {
  agreement: "Agreements",
  form: "Forms",
  checklist: "Checklists",
  letter: "Letters",
  email: "Emails",
  outreach: "Outreach",
  script: "Scripts",
};

export default function Templates() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  const [content, setContent] = useState("");
  const [creating, setCreating] = useState(false);
  const [newT, setNewT] = useState({ name: "", kind: "email", content: "" });

  const load = async () => {
    const { data } = await api.get("/success/templates");
    setItems(data.items || []);
  };
  useEffect(() => { load(); }, []);

  const open = (t) => { setSelected(t); setContent(t.content); };

  const save = async () => {
    await api.put(`/success/templates/${selected.id}`, { content });
    toast.success("Template saved");
    load();
  };

  const copy = () => {
    navigator.clipboard.writeText(content);
    toast.success("Copied to clipboard");
  };

  const create = async (e) => {
    e.preventDefault();
    const { data } = await api.post("/success/templates", newT);
    setCreating(false);
    setNewT({ name: "", kind: "email", content: "" });
    load();
    open(data);
  };

  const remove = async (id) => {
    if (!confirm("Delete this template?")) return;
    await api.delete(`/success/templates/${id}`);
    if (selected?.id === id) setSelected(null);
    load();
  };

  const filtered = items.filter(t => !q || t.name.toLowerCase().includes(q.toLowerCase()) || t.content.toLowerCase().includes(q.toLowerCase()));
  const grouped = filtered.reduce((acc, t) => {
    (acc[t.kind] = acc[t.kind] || []).push(t);
    return acc;
  }, {});

  return (
    <div>
      <div className="mb-6 flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="eyebrow mb-2">Success Center</div>
          <h1 className="font-display text-3xl font-bold text-navy">Resource Library</h1>
          <p className="text-sm text-slate-500 mt-1">Editable templates: agreements, forms, emails, scripts. Use <code className="bg-slate-100 px-1 py-0.5 rounded text-xs">{'{PLACEHOLDER}'}</code> variables.</p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-navy"><Plus className="w-4 h-4" /> New template</button>
      </div>

      <div className="grid md:grid-cols-12 gap-6 h-[70vh]">
        {/* Left: templates list */}
        <div className="md:col-span-4 border border-slate-200 rounded-xl bg-white overflow-hidden flex flex-col">
          <div className="p-3 border-b relative">
            <Search className="absolute left-6 top-6 w-4 h-4 text-slate-400" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search templates…"
              className="pl-8 w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
          </div>
          <div className="flex-1 overflow-y-auto">
            {Object.entries(grouped).map(([kind, list]) => (
              <div key={kind}>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 pt-4 pb-1">{KIND_LABELS[kind] || kind}</div>
                {list.map(t => (
                  <button key={t.id} onClick={() => open(t)}
                    className={`w-full text-left p-3 hover:bg-slate-50 border-b flex items-center gap-2 ${selected?.id === t.id ? "bg-slate-50" : ""}`}>
                    <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <div className="text-sm text-navy truncate">{t.name}</div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Right: editor */}
        <div className="md:col-span-8 border border-slate-200 rounded-xl bg-white flex flex-col">
          {selected ? (
            <>
              <div className="p-4 border-b flex items-center justify-between">
                <div>
                  <div className="font-display font-semibold text-navy">{selected.name}</div>
                  <div className="text-xs text-slate-500 uppercase">{KIND_LABELS[selected.kind] || selected.kind}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={copy} className="text-sm border border-slate-200 rounded-md px-3 py-1.5 hover:border-navy inline-flex items-center gap-1"><Copy className="w-3.5 h-3.5" /> Copy</button>
                  <button onClick={save} className="btn-navy py-1.5 px-3 text-sm"><Save className="w-3.5 h-3.5" /> Save</button>
                  <button onClick={() => remove(selected.id)} className="text-sm text-red-500 hover:text-red-700 p-2"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <textarea value={content} onChange={e => setContent(e.target.value)}
                className="flex-1 p-4 text-sm font-mono focus:outline-none resize-none" />
            </>
          ) : (
            <div className="flex-1 grid place-items-center text-slate-400 text-sm">Select a template to edit</div>
          )}
        </div>
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setCreating(false)}>
          <form onSubmit={create} className="bg-white rounded-xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="font-display text-xl font-semibold text-navy mb-4">New template</div>
            <div className="space-y-3 text-sm">
              <div>
                <label className="text-xs text-slate-500">Name</label>
                <input required value={newT.name} onChange={e => setNewT({...newT, name: e.target.value})} className="w-full border rounded px-3 py-2 mt-1" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Type</label>
                <select value={newT.kind} onChange={e => setNewT({...newT, kind: e.target.value})} className="w-full border rounded px-3 py-2 mt-1">
                  {Object.entries(KIND_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500">Content</label>
                <textarea rows={8} value={newT.content} onChange={e => setNewT({...newT, content: e.target.value})}
                  className="w-full border rounded px-3 py-2 mt-1 font-mono text-sm" />
              </div>
              <button type="submit" className="btn-navy w-full justify-center">Create template</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
